import React, { useContext, useState, useEffect, useMemo } from "react";
import { supabase } from "../supabase";
import * as API from "../api";
import { useAuth } from "./AuthContext";

const fieldsToSelect =
  // eslint-disable-next-line max-len
  "id, first_name, surname, phone, parent_id, is_leader, email_verified, phone_verified, refs_user_lifecycle_status_id, users_supplier_index(suppliers_master(*)), users_product_type_index(refs_product_type(*))";

const StructureContext = React.createContext({
  downstream: [],
  downstreamArrayState: [],
});

/**
 * Custom hook to access the current value of the StructureContext.
 *
 * @returns {object} The current context value, typically containing the downstream data.
 */
export function useStructure() {
  return useContext(StructureContext);
}

/**
 * Provides the structure context to its children components, managing the state and retrieval of
 * downstream and upstream dealer data from the database. It fetches and updates downstream data
 * whenever changes occur in the "dealer_downstream" table and also provides functions to fetch
 * downstream and upstream data for specific dealers.
 *
 * @param {object} props - The component props.
 * @param {React} props.children - The child components that can access the structure context.
 */
export function StructureProvider({ children }) {
  const [downstream, setDownstream] = useState([]);
  const [downstreamIDs, setDownstreamIDs] = useState([]);
  const { currentUser, authSession } = useAuth();
  /**
   * Fetches the downstream data for the currently logged in user.
   * Calls the liveStructure API endpoint and splits the returned downstream array
   * into chunks of 1000 elements before making a request to Supabase for each chunk
   * to fetch the dealer data. The fetched data is then combined into a single array
   * and set as the downstream state.
   * If there is an error with the API request or the Supabase request, the downstream
   * state is reset to an empty array.
   */
  const fetchDownstream = async () => {
    try {
      const response = await API.getLiveStructure(
        currentUser.is_admin
          ? {
              user_id: currentUser.id,
              is_admin: true,
            }
          : {
              user_id: currentUser.id,
              is_admin: false,
            }
      );

      if (!response.data || response.data.length === 0) {
        setDownstream([]);
      }

      const downstreamArray = response.data[0].user_id_downstream || [];

      setDownstreamIDs(downstreamArray);
      const chunkSize = 100;

      // Helper function to split an array into chunks
      const chunkArray = (array, size) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
          chunks.push(array.slice(i, i + size));
        }
        return chunks;
      };

      const downStreamChunks = chunkArray(downstreamArray, chunkSize);

      // Helper function to fetch chunk data from Supabase
      const fetchChunkData = async chunk => {
        const { data: userData, error: userError } = await supabase
          .schema(process.env.REACT_APP_SB_SCHEMA)
          .from("users_master")
          .select(fieldsToSelect)
          .in("id", chunk)
          .neq("refs_user_lifecycle_status_id", 4);

        if (userError) throw userError;

        return userData;
      };

      // Fetch downstream data
      const downstreamPromises = downStreamChunks.map(fetchChunkData);
      const downStreamChunkDataResults = await Promise.all(downstreamPromises);
      const finalDownstream = downStreamChunkDataResults.flat();

      setDownstream(finalDownstream);
    } catch (error) {
      console.error("Error fetching dealer structure:", error);
      setDownstream([]);
    }
  };

  /**
   * Recursively assigns a structure level to each item in the given data array.
   * Structure level is determined by the parent_dealer_id of each item.
   * The top-level item (with parent_dealer_id === null) starts at structure level 1.
   * The function then assigns levels to the children of each item, starting from the
   * top and working its way down.
   * @param {Array} data - An array of objects with id and parent_dealer_id properties
   * @returns {Array} - The same array with structure_level property added to each item
   */
  function assignStructureLevels(data) {
    // Recursive function to assign structure level
    // eslint-disable-next-line jsdoc/require-jsdoc
    function setLevel(item, level) {
      if (item) {
        // eslint-disable-next-line no-param-reassign
        item.structure_level = level;
        // Find children of the current item and assign levels
        data
          .filter(child => child.parent_dealer_id === item.id)
          .forEach(child => setLevel(child, level + 1));
      }
    }

    // Find the top-level item (where parent_dealer_id is null)
    const topLevelItem = data.find(item => item.parent_dealer_id === null);

    // Start assigning levels from the top
    setLevel(topLevelItem, 1);

    return data;
  }

  /**
   * Fetches the dealer structure for a given dealer ID. If the current user is an admin,
   * it fetches all dealers from the database. Otherwise, it retrieves the upstream and
   * downstream structure for the specified dealer ID.
   *
   * @param {number|string} user_id - The ID of the dealer to fetch the structure for.
   * @returns {Promise<{upstream: Array<object>, downstream: Array<object>}>}
   * An object containing two arrays:
   *   - `upstream`: The structure of upstream dealers.
   *   - `downstream`: The structure of downstream dealers.
   *
   * @throws {Error} Throws an error if there is an issue fetching data from the database or API.
   */
  const fetchOtherDealerStructure = async user_id => {
    // Check if the current user is an admin or if admin check should be skipped
    try {
      // Fetch the live structure for the specified dealer ID

      const dealerList = await API.getLiveStructure({
        // eslint-disable-next-line object-shorthand
        user_id: user_id,
        idToken: authSession.token,
      });

      if (!dealerList.data || dealerList.data.length === 0) {
        return { upstream: [], downstream: [] };
      }

      const downstreamArray = dealerList.data[0].user_id_downstream || [];
      const upstreamArray = dealerList.data[0].user_id_upstream || [];
      const chunkSize = 1000;

      const chunkArray = (array, size) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
          chunks.push(array.slice(i, i + size));
        }
        return chunks;
      };

      const downStreamChunks = chunkArray(downstreamArray, chunkSize);
      const upstreamChunks = chunkArray(upstreamArray, chunkSize);

      // Helper function to fetch chunk data from Supabase
      const fetchChunkData = async chunk => {
        const { data: userData, error: userError } = await supabase
          .schema(process.env.REACT_APP_SB_SCHEMA)
          .from("users_master")
          .select(fieldsToSelect)
          .in("id", chunk)
          .neq("refs_user_lifecycle_status_id", 4);

        if (userError) throw userError;
        return userData;
      };

      // Fetch downstream data
      const downstreamPromises = downStreamChunks.map(fetchChunkData);
      const downStreamChunkDataResults = await Promise.all(downstreamPromises);
      const finalDownstream = downStreamChunkDataResults.flat();

      // Fetch upstream data
      const upstreamPromises = upstreamChunks.map(fetchChunkData);
      const upstreamChunkDataResults = await Promise.all(upstreamPromises);
      const finalUpstream = upstreamChunkDataResults.flat();

      // Return the results
      return {
        upstream: assignStructureLevels(finalUpstream),
        downstream: finalDownstream,
      };
    } catch (error) {
      console.error("Error fetching dealer structure:", error);
      return { upstream: [], downstream: [] };
    }
  };

  useEffect(() => {
    fetchDownstream();
  }, []);

  const value = {
    downstream,
    downstreamIDs,
    fetchOtherDealerStructure,
  };

  const memoizedValue = useMemo(() => value, [value]);

  return (
    <StructureContext.Provider value={memoizedValue}>
      {children}
    </StructureContext.Provider>
  );
}
